import * as React from 'react'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'

import { DriverCalendarDayModel } from 'app/models/calendar/driverCalendarDayModel'
import { CalendarOption } from 'app/constants/calendarOptions'

import { useActivity } from '@/modules/drivers/stores/activityStore'
import { useDayTypePalette } from '@/modules/drivers/stores/dayTypePaletteStore'
import { CalendarDayCell } from './CalendarDayCell'

export { COLORS } from './CalendarDayCell'

export type DayItemProps = {
  translate: (text) => any
  day: DriverCalendarDayModel
  height: number
  width: number
  showViolations?: boolean
  selectedOptions?: Array<CalendarOption>
  onSelected: (day: DriverCalendarDayModel, e: any) => void
  onMouseDown?: (day: DriverCalendarDayModel, e: React.MouseEvent) => void
  onMouseEnterDay?: (day: DriverCalendarDayModel) => void
  onCalendarAction: (
    day: DriverCalendarDayModel,
    action: CalendarMenuAction,
    data?: any,
  ) => void
  isSelected: boolean
  isMultiSelected?: boolean

  multiSelectMode?: boolean
  refreshCalendar?: () => Promise<void>
  onContextMenuRequest?: (
    day: DriverCalendarDayModel,
    anchor: { x: number; y: number },
  ) => void
  isContextMenuOpen?: boolean
  isFirstRow?: boolean
  isLastInRow?: boolean
  showTrips: boolean
}

export enum CalendarMenuAction {
  GoToDayChart = 'GoToDayChart',
  GoToWeekChart = 'GoToWeekChart',
  ShowChart = 'ShowChart',
  ShowDetails = 'ShowDetails',
  GenerateAttestation = 'GenerateAttestation',
  ChangeDayType = 'ChangeDayType',
  RestoreOriginalCardData = 'RestoreOriginalCardData',
  RemoveDigitalData = 'RemoveDigitalData',
  RemoveBorderCrossings = 'RemoveBorderCrossings',
  RemoveTmsData = 'RemoveTmsData',
  AssignTmsDataToDriverAutomatically = 'AssignTmsDataToDriverAutomatically',
  AssignBorderCrossingsFromVehicle = 'AssignBorderCrossingsFromVehicle',
  InsertActivityFromAnotherDay = 'InsertActivityFromAnotherDay',
  PrintApplications = 'PrintApplications',
}

export const CalendarDayItem: React.FC<DayItemProps> = React.memo((props) => {
  const {
    day,
    height,
    width,
    onSelected,
    onMouseDown,
    onMouseEnterDay,
    isSelected,
    isMultiSelected,
    multiSelectMode,
    showViolations = true,
    onContextMenuRequest,
    isContextMenuOpen,
    isFirstRow = false,
    isLastInRow = false,
    showTrips = false,
  } = props
  const paletteId = useDayTypePalette()
  const [isHovered, setIsHovered] = React.useState(false)
  const [isAnyModalOpen, setIsAnyModalOpen] = React.useState(false)

  React.useEffect(() => {
    const checkModalState = () => {
      setIsAnyModalOpen(document.body.hasAttribute('data-modal-open'))
    }
    checkModalState()
    const observer = new MutationObserver(checkModalState)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-modal-open'],
    })
    return () => observer.disconnect()
  }, [])

  const clickCountRef = React.useRef(0)
  const clickTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const isEmpty = day.dayNumber === 0
  const { showTmsData } = useActivity()

  const formattedDate = moment(day.activityDayLocalTimeDate).format(
    'YYYY-MM-DD',
  )
  const navigate = useNavigate()
  const { companyId, driverId } = useParams<{
    companyId: string
    driverId: string
  }>()
  const handleGoToDailyChart = () => {
    navigate(
      `/et/company/${companyId}/driverModule/activities/driver/${driverId}/dailyActivity/${formattedDate}`,
    )
  }
  return (
    <CalendarDayCell
      day={day}
      height={height}
      width={width}
      paletteId={paletteId}
      showViolations={showViolations}
      showTrips={showTrips}
      showTmsData={showTmsData}
      isSelected={isSelected}
      isMultiSelected={isMultiSelected}
      multiSelectMode={multiSelectMode}
      isHovered={isHovered}
      isFirstRow={isFirstRow}
      isLastInRow={isLastInRow}
      onMouseMove={
        isEmpty
          ? undefined
          : (e) => {
              if (e.buttons) document.getSelection().removeAllRanges()
            }
      }
      onContextMenu={
        isEmpty
          ? undefined
          : (e) => {
              if (isAnyModalOpen) {
                e.preventDefault()
                e.stopPropagation()
                return
              }
              e.preventDefault()
              e.stopPropagation()

              onContextMenuRequest?.(day, { x: e.clientX, y: e.clientY })
            }
      }
      onMouseDown={
        isEmpty
          ? undefined
          : (e) => {
              if (e.button === 2) return

              e.preventDefault()
              onMouseDown?.(day, e)
            }
      }
      onDragStart={(e) => e.preventDefault()}
      onMouseEnter={
        isEmpty
          ? undefined
          : () => {
              setIsHovered(true)
              onMouseEnterDay?.(day)
            }
      }
      onMouseLeave={() => setIsHovered(false)}
      onClick={
        isEmpty
          ? undefined
          : (e) => {
              if (isAnyModalOpen) {
                e.preventDefault()
                e.stopPropagation()
                return
              }
              if (isContextMenuOpen) {
                return
              }
              onSelected?.(day, e)
              document.getSelection().removeAllRanges()
            }
      }
      onDoubleClick={(e) => {
        e.persist()
        const snapshotDay = day
        clickTimerRef.current = setTimeout(() => {
          if (clickCountRef.current === 1) {
            onSelected?.(snapshotDay, e)
            document.getSelection()?.removeAllRanges()
          } else {
            handleGoToDailyChart()
          }
          clickCountRef.current = 0
        }, 250)
      }}
    />
  )
})
